import React, { useEffect } from "react";
import { MainTracker } from "../../PixelTrackers/MainTracker";
import { categoryBucket, setCustomData, setPageData } from "../../PixelTrackers/KameleoonTracker";
import { useLocationContext } from '../../App';
import { useLocation } from "react-router-dom";
import { useCartContext } from "../../customHooks/useCart";
import { setKameleoonCustomerData } from "../../customHooks/useCustomer";
import * as http from "../../utils/http";
import { getFunnelMentionRedirectUrlForLocation } from "../../utils/funnelMentionRedirects";
import { captureFlowzzTrackingProps } from "../../utils/flowzzTracking";

let location = {};

// Flowzz links carry af=flowzz|flowzz_premium and up to three product slots p1..p3
// (Premium links have no epd, so `af` is the only reliable marker).
// Start-consultation is the landing page, so it captures the Flowzz props for the later funnel pages (HL-12516).
const getFlowzzProps = (urlParams) => {
  const flowzzProps = captureFlowzzTrackingProps();
  // Cannaleo links carry the same p1..p3 slots, so only count them for Flowzz
  const productCount = flowzzProps["Came from Flowzz?"] === "Yes" ? ["p1", "p2", "p3"].filter((slot) => urlParams.get(slot)).length : 0;
  return {
    ...flowzzProps,
    "Number of Flowers from Flowzz": productCount,
  };
};

const KAMELEOON_SYNC_POLL_MS = 1000;

// HL-11260 #4: consentMode (our banner) is the source of truth for Kameleoon consent.
// Unknown or rejected consent => the visitor code must not be persisted anywhere, including our server cookie.
const hasKameleoonConsent = () => {
  try {
    const consentMode = JSON.parse(localStorage.getItem("consentMode"));
    return consentMode?.analytics_storage === "granted";
  } catch (e) {
    return false;
  }
};

const pageTranslations = {
  questionnaire: "View Questionnaire Page",
  "e-recipe-upload": "View E-recipe-upload page",
  sign_up: "View Checkout Page",
  select_treatment: "View Choose Treatment Page",
  "choose-doctor": "View Choose Appointment Page",
  shipping: "View Page: shipping",
  payment: "View Payment Page",
  login:"View Login Page",
  register:"View Registration Page",
  "thank-you": "View Thank You Page"
};


const blacklistPaths = ["reorder"]

// Kameleoon page_type custom data per first path segment; paths not listed send nothing.
const kameleoonPageTypes = {
  "start-consultation": "start_consultation",
  questionnaire: "questionnaire",
  select_treatment: "choose_treatment",
  select_treatment_landing_page: "choose_treatment",
  care_plus: "choose_treatment",
  choose_plan: "choose_treatment",
  shipping: "shipping",
  payment: "payment",
  payment_v2: "payment",
  "thank-you": "thank_you",
  account: "my_account",
};

// Routes shaped /<path>/:country/:language/:category|:mention
const categoryRoutePaths = ["start-consultation", "choose-doctor", "questionnaire", "select_treatment", "select_treatment_landing_page", "care_plus", "choose_plan"];

// Set before any pageview fires: Kameleoon evaluates once per page (single page support),
// so custom data must be in the queue by the time the route is tracked.
const setKameleoonPageData = (site, path, categorySlug) => {
  const country = site?.toLowerCase() === "uk" ? "gb" : site?.toLowerCase(); // UK funnel URLs carry "uk"
  if (["de", "fr", "ch", "gb"].includes(country)) {
    setCustomData("country", country);
  }
  if (kameleoonPageTypes[path]) {
    setPageData("page_type", kameleoonPageTypes[path]); // read by Kameleoon at evaluation time, survives the SPA reload
  }
  if (categoryRoutePaths.includes(path) && categorySlug) {
    setCustomData("category_slug", categorySlug);
    setCustomData("category", categoryBucket(categorySlug));
  }
  setKameleoonCustomerData(); // is_logged_in + customer_type / numbers (HL-12604)
};

const getDeviceType = () => {
  const userAgent = navigator.userAgent;

  if (/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent)) {
    return "mobile";
  } else if (/iPad/i.test(userAgent)) {
    return "tablet";
  } else {
    return "desktop";
  }
}

const PageTracker = () => {
  const { setLocation } = useLocationContext();
  const fullLocation = useLocation();
  const { getSessionFields } = useCartContext();

  useEffect(() => {
    setLocation(fullLocation)
  }, [fullLocation]);

  useEffect(() => {
    window.kameleoonQueue?.push(["Core.enableSinglePageSupport"]);

    // Safari ITP workaround (HL-11260 #6): sync the kameleoonVisitorCode
    // to a server-set cookie once per session, so it survives Safari's 7-day JS-storage wipe.
    // Keeps polling (without syncing) until statistics consent is granted, so accepting the
    // banner mid-session still triggers the sync.
    const syncInterval = setInterval(() => {
      if (sessionStorage.getItem("kameleoonVisitorCodeSynced")) {
        clearInterval(syncInterval);
        return;
      }
      if (!hasKameleoonConsent()) {
        return;
      }
      const kameleoonVisitorCode = window.Kameleoon?.API?.Visitor?.code || localStorage.getItem("kameleoonVisitorCode");
      if (kameleoonVisitorCode) {
        http.postToServer("t/kameleoon-visitor-sync", { kameleoonVisitorCode }, [])
          .then(() => {
            sessionStorage.setItem("kameleoonVisitorCodeSynced", "1");
            clearInterval(syncInterval);
          })
          .catch(() => {});
      }
    }, KAMELEOON_SYNC_POLL_MS);

    const handlePopStateTracking = () => {
      window.eventsTracking.track({e: "Click Go Back", p: {location: location.pathname} });
    };
    window.addEventListener("popstate", handlePopStateTracking);
    return () => {
      clearInterval(syncInterval);
      window.removeEventListener("popstate", handlePopStateTracking);
    };
  }, []);

    // A funnel entry page whose mention is about to be redirected (legacy
    // weight_loss -> weight_loss_2) must not be tracked here. This component
    // renders above Routes and tracks during render, so it would otherwise fire
    // a pageview for a URL FunnelMentionRedirect replaces before it ever
    // mounts — double-counting the pageview once the target URL tracks too.
    // Returning before location.pathname is stamped keeps the target trackable.
    if (getFunnelMentionRedirectUrlForLocation(window.location)) {
      return <></>;
    }

    if (window.location.pathname !== location.pathname) {
      location.pathname = window.location.pathname;

      let fullPath = location.pathname;
      let splits = fullPath.split("/");
      

      let path
      let site
      // in case of country is the first path
      if (splits[1].length === 2){
        site = splits[1]
        path = splits[2]
      }
      else{
        path = splits[1]
        site = splits[2]
        
      }

      if(blacklistPaths.includes(path)){
        return <></>;
      }

      if (site && site.length === 2){
        MainTracker.superProperties({Site: site.toUpperCase()});
      }
      setKameleoonPageData(site && site.length === 2 ? site : null, path, splits[4]);

      const urlParams = new URLSearchParams(window.location.search);
      const psp = urlParams.get('psp');
      const pm = urlParams.get('pm');
      // if there is the psp param in the url, add super property for the psp and payment method
      if (psp) {
        MainTracker.superProperties({ 'Payment Service Provider': psp.charAt(0).toUpperCase() + psp.slice(1), 'Payment Method': pm });
      } 
      // checkout=primer: the payment went through Primer's checkout, psp is the processor it routed to
      const checkout = urlParams.get('checkout');
      if (checkout) {
        MainTracker.superProperties({ 'Checkout Provider': checkout.charAt(0).toUpperCase() + checkout.slice(1) });
      }
      let translatedPath = pageTranslations[path] || 'View ' +  path + ' Page';
      addSuperProperties(translatedPath,fullPath)

      // Skip tracking for "payment" - it's tracked manually in usePaymentData with A/B test variant
      if (path === "payment" || path === "payment_v2") {
        return <></>;
      }
      // Skip tracking for "shipping" - it's tracked manually in ShippingV2.tsx after the shipping GET resolves
      if (path === "shipping") {
        return <></>;
      }
      // Skip tracking for "questionnaire" - it's tracked manually in Questionnaire.tsx after server data loads (Mixpanel Category Catalog)
      if (path === "questionnaire") {
        return <></>;
      }
      // Skip tracking for "select_treatment" - it's tracked manually in ChooseTreatmentRouter
      // after server data loads, so the service_fee_test variant resolved during that GET is
      // available (HL-9738). Firing here races ahead of the variant and loses the property.
      if (path === "select_treatment") {
        return <></>;
      }
      // Skip tracking for "thank-you" - it's tracked manually in Thankyou.tsx after server data loads (Care+ properties)
      if (path === "thank-you") {
        return <></>;
      }

      if (path === "account" || path === "login" || path === "checkout" || path === "payment_page_callback") {
        window.eventsTracking.track({e: translatedPath });
      }
      else if (path === "start-consultation") {
        MainTracker.track("pv", translatedPath, getFlowzzProps(urlParams));
      }
      else {
        MainTracker.track("pv", translatedPath);
      }
    }

  function addSuperProperties(translatedPath, fullPath) {
    
    let sessionCart
    while(!sessionCart){
      sessionCart = getSessionFields();
    }

    if (translatedPath == "View Choose Treatment Page") {
      MainTracker.superProperties({
        "Product Catalog": sessionCart.mention,
        "Mixpanel Category Catalog": sessionCart.mixpanel_title,
        "E-Rezept": sessionCart.is_e_recipe ? "Yes" : "No"
      });
    }

    MainTracker.superProperties({
      "Device Type": getDeviceType(),
      "Is Logged In?": window.eventsTracking.getData("customerID") ? "Yes" : "No"
    });

  }

  return <></>;
};

export default PageTracker;
